/**
 * One prescription cell.
 *
 * ── The draft lives HERE and nowhere else ────────────────────────────────────────────────────
 * A cell is a controlled input over the store until the moment the trainer starts typing, at which
 * point it holds its own string until they stop. That is not a convenience: the store's value is a
 * NUMBER, and "8." or "-" or "" are all legitimate things to be halfway through typing that no
 * number can represent. Writing every keystroke through `commitSetCell` would re-format the field
 * under the caret — a trainer typing "0,5" would watch it become "0" after the comma.
 *
 * So: local while focused, the store's the rest of the time. The local draft is dropped on commit,
 * which is what makes the store the single source of truth between edits.
 *
 * ── Three exits, and each one means something different ──────────────────────────────────────
 * · **Blur** commits. Walking to the next cell is how a trainer fills a row.
 * · **Enter** commits, and the table then moves the caret one row DOWN. It stayed put until 8
 *   September 2026, on the reasoning that moving on Enter would make it the same key as Tab — which
 *   was true of the browser's Tab and is not true of the mock's: there Enter is `ArrowDown` and Tab
 *   is a walk along the row (`model/setTableNavigation`). The commit is still this component's and
 *   still happens FIRST: the table's handler is bound on the bubble, so by the time it moves focus
 *   the value is in the store.
 * · **Escape** abandons and stops the key there — the dock listens for Escape to close, and an
 *   editor must absorb the key that closes it or a mistyped weight would also shut the panel.
 *
 * A commit that changes nothing writes nothing the trainer can see: the store's own no-op guard
 * (Immer returns no patches when the recipe changed nothing) is what keeps opening a cell and
 * leaving it from marking the plan unsaved.
 *
 * ── Two destinations, and the cell is told which rather than deciding ─────────────────────────
 * `cell.pin` is the whole difference between the builder and the calendar's day editor. Absent, a
 * commit writes the AUTHORED set, which every cycle reads. Present, it pins this cycle alone —
 * `setMatrixOverride` owns every rule about that, including clearing a pin that merely restates what
 * the rule already computes. The parse and the clamp are the same either way: `commitSetCell` is the
 * glass, and a pin is the same number the base would have taken.
 */

import { useState } from 'react';

import { setMatrixOverride, setSetField, useIsAiWrittenValue, type SetAddress } from '~/entities/plan-draft';
import aiMark from '~/shared/ui/aiWritten.module.css';

import { commitSetCell } from '../model/setCellValue';
import type { SetCellView } from '../model/dayProgramming';

import { AI_WRITTEN_NOTE_ID } from './aiWrittenNote';
import styles from './SetTable.module.css';

interface SetCellProps {
  readonly cell: SetCellView;
  readonly address: SetAddress;
  /** The day's concrete rest, for the cleared-rest rule. Ignored by every other cell. */
  readonly dayRestSeconds: number;
  /**
   * The day is being READ — the client calendar's preview of a session that has not happened.
   *
   * The field stays a field rather than becoming text: it keeps the table's geometry, it keeps the
   * arrow navigation that reads a long day, and its value stays selectable. What goes is the write.
   */
  readonly readOnly: boolean;
}

export function SetCell({ cell, address, dayRestSeconds, readOnly }: SetCellProps) {
  const [draft, setDraft] = useState<string | null>(null);
  // A base value Buddy wrote, until the trainer edits the exercise (ai-plan-builder R15; board S2).
  const aiWritten = useIsAiWrittenValue(address.exerciseSlotToken, address.setIndex, cell.contract.valueKey) && cell.pin === null;

  const commit = () => {
    if (draft === null) return;
    const patch = commitSetCell(cell.contract, draft, dayRestSeconds);
    setDraft(null);
    const pin = cell.pin;
    if (pin === null) {
      setSetField(address, patch);
      return;
    }
    // The glass already parsed, scaled and clamped it; the pin takes that same number. Reading it
    // back off the patch rather than re-parsing is what stops the two paths disagreeing about what
    // "87,5 %" means in wire units. A pin is only ever offered on a numeric progression field
    // (`cyclePrescription`'s `pinField`), so anything else is a cleared cell.
    const value = patch[cell.contract.valueKey];
    setMatrixOverride(
      address.exerciseSlotToken,
      { setIndex: address.setIndex, cycleIndex: pin.cycleIndex, baseCycle: pin.baseCycle },
      typeof value === 'number' ? value : null,
    );
  };

  if (cell.resolvedNote !== null) {
    // Nothing to edit and nothing honest to show: the value does not exist until the client starts
    // the workout. A sentence rather than a disabled field carrying an estimate.
    return (
      <span className={`${String(styles.cell)} ${String(styles.secondary)}`}>
        <span className={styles.resolved}>{cell.resolvedNote}</span>
      </span>
    );
  }

  return (
    <span className={`${String(styles.cell)} ${cell.emphasis === 'secondary' ? String(styles.secondary) : ''}`}>
      {/* `data-unit` when the cell carries one, and it is a LAYOUT fact rather than a decoration:
        * a full-width field pushes the unit to the cell's right edge, so "60" and "kg" read as two
        * columns instead of as one value. See `.cellRow[data-unit]` in the stylesheet. */}
      <span className={styles.cellRow} data-unit={cell.suffix === null ? undefined : ''}>
        <input
          className={`${String(styles.input)} ${aiWritten ? String(aiMark.aiWritten) : ''}`}
          aria-describedby={aiWritten ? AI_WRITTEN_NOTE_ID : undefined}
          // `text` rather than `number`: a number input swallows the comma a Polish keyboard puts
          // under the trainer's thumb, refuses "2:30" outright, and adds a spinner the canon does
          // not draw. `inputMode` is what actually summons the right keypad.
          type="text"
          inputMode={cell.inputMode}
          autoComplete="off"
          value={draft ?? cell.text}
          aria-label={cell.ariaLabel}
          readOnly={readOnly}
          onChange={(event) => {
            if (readOnly) return;
            setDraft(event.target.value);
          }}
          onBlur={readOnly ? undefined : commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
            if (event.key === 'Escape') {
              event.stopPropagation();
              setDraft(null);
            }
          }}
        />
        {cell.suffix !== null && (
          // Decorative: the unit is already in the cell's accessible name, and repeating it here
          // would make a screen reader read "Ciężar, seria 3, 82,5 kilogram kg".
          <span className={styles.suffix} aria-hidden="true">
            {cell.suffix}
          </span>
        )}
      </span>
      {/* The derived estimate. NOT `aria-hidden`: it is the only thing on screen saying this load
        * does not exist yet, and a trainer who cannot see it would read the percentage as a
        * prescription. */}
      {cell.derived !== null && <span className={styles.derived}>{cell.derived}</span>}
    </span>
  );
}
